import { useCallback, useEffect, useState } from 'react';
import { tx } from '../../../lib/adminI18n';
// 清掃チェックリストの管理（清掃管理画面の「チェックリスト」タブ）  2026-10-31
//   全物件共通の項目＋物件ごとの追加項目。清掃者は完了報告のとき全項目にチェックが必要
interface PropertyOpt { id: string; name: string; }
interface Item { id: number; property_id: string | null; property_name: string | null; label: string; sort_order: number; is_active: number; }

async function api(path: string, method = 'GET', body?: unknown) {
  const res = await fetch(`/api/admin/cleaning/checklist-items${path}`, {
    method, credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const d = await res.json().catch(() => ({} as any));
  if (!res.ok) throw new Error(d?.error || `Error ${res.status}`);
  return d;
}

export default function ChecklistManager({ properties }: { properties: PropertyOpt[] }) {
  const [items, setItems] = useState<Item[]>([]);
  const [scope, setScope] = useState('');           // '' = 全物件共通
  const [label, setLabel] = useState('');
  const [editId, setEditId] = useState<number | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const d = await api(''); setItems(d.items || []); } catch (e: any) { setErr(e.message); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function run(f: () => Promise<unknown>) {
    setBusy(true); setErr('');
    try { await f(); await load(); } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }
  const list = items.filter((i) => (i.property_id || '') === scope);
  const common = items.filter((i) => !i.property_id && i.is_active);

  function move(idx: number, dir: -1 | 1) {
    const a = list[idx], b = list[idx + dir];
    if (!a || !b) return;
    void run(async () => {
      await api(`/${a.id}`, 'PATCH', { sort_order: b.sort_order });
      await api(`/${b.id}`, 'PATCH', { sort_order: a.sort_order === b.sort_order ? a.sort_order + dir : a.sort_order });
    });
  }

  return (
    <div className="cln-detail" style={{ maxWidth: 760 }}>
      <h2>{tx('clnCk.title')}</h2>
      <p className="cln-modal-sub">{tx('clnCk.desc')}</p>
      <label className="cln-label">{tx('clnCk.scope')}
        <select className="cln-input" value={scope} onChange={(e) => { setScope(e.target.value); setEditId(null); }}>
          <option value="">{tx('clnCk.common')}</option>
          {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </label>
      {scope && common.length > 0 && (
        <p className="cln-modal-sub">{tx('clnCk.inherit', { n: common.length })}</p>
      )}
      {err && <p className="cln-msg cln-msg-error">⚠ {err}</p>}
      <div style={{ display: 'grid', gap: 6, margin: '12px 0' }}>
        {list.length === 0 && <p className="cln-empty">{tx('clnCk.empty')}</p>}
        {list.map((it, idx) => (
          <div key={it.id} className="cln-detail-row" style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: it.is_active ? 1 : 0.5 }}>
            <span style={{ width: 24, textAlign: 'right', color: '#6b7280' }}>{idx + 1}.</span>
            {editId === it.id ? (
              <input className="cln-input" style={{ flex: 1 }} value={editLabel} autoFocus onChange={(e) => setEditLabel(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void run(async () => { await api(`/${it.id}`, 'PATCH', { label: editLabel }); setEditId(null); }); }} />
            ) : (
              <span style={{ flex: 1 }}>☑ {it.label}{!it.is_active && ` (${tx('clnCk.inactive')})`}</span>
            )}
            {editId === it.id ? (<>
              <button className="cln-btn cln-btn-primary" disabled={busy} onClick={() => void run(async () => { await api(`/${it.id}`, 'PATCH', { label: editLabel }); setEditId(null); })}>{tx('clnCk.save')}</button>
              <button className="cln-btn cln-btn-ghost" disabled={busy} onClick={() => setEditId(null)}>{tx('clnCk.cancel')}</button>
            </>) : (<>
              <button className="cln-btn cln-btn-ghost" disabled={busy || idx === 0} onClick={() => move(idx, -1)} aria-label="up">↑</button>
              <button className="cln-btn cln-btn-ghost" disabled={busy || idx === list.length - 1} onClick={() => move(idx, 1)} aria-label="down">↓</button>
              <button className="cln-btn cln-btn-ghost" disabled={busy} onClick={() => { setEditId(it.id); setEditLabel(it.label); }}>{tx('clnCk.edit')}</button>
              <button className="cln-btn cln-btn-ghost" disabled={busy} onClick={() => void run(() => api(`/${it.id}`, 'PATCH', { is_active: !it.is_active }))}>
                {it.is_active ? tx('clnCk.disable') : tx('clnCk.enable')}</button>
              <button className="cln-btn cln-btn-ghost" disabled={busy} onClick={() => { if (window.confirm(tx('clnCk.confirmDel', { s: it.label }))) void run(() => api(`/${it.id}`, 'DELETE')); }}>{tx('clnCk.del')}</button>
            </>)}
          </div>
        ))}
      </div>
      <form style={{ display: 'flex', gap: 8 }} onSubmit={(e) => { e.preventDefault(); if (!label.trim()) return; void run(async () => { await api('', 'POST', { label, property_id: scope || null }); setLabel(''); }); }}>
        <input className="cln-input" style={{ flex: 1 }} value={label} maxLength={200} placeholder={tx('clnCk.ph')} onChange={(e) => setLabel(e.target.value)} />
        <button type="submit" className="cln-btn cln-btn-primary" disabled={busy || !label.trim()}>{tx('clnCk.add')}</button>
      </form>
    </div>
  );
}
